import React, { useCallback, useEffect, useRef, useState } from "react";
import { Routes, Route } from "react-router-dom";

import { Helmet } from "react-helmet";
// components
import AuthGuard from "auth-guard";
import Login from "components/login/Login";
import AbhaysForm from "components/abhyasiGeneralInfoForm/abhyasi-donors-general-information";
import HFNErrorBoundary from "components/errorBoundary";
import HFNLoader from "sharedComponents/lazyLoading";
import HFNToaster from "sharedComponents/toasters";
import HFNConfirmDialog from "sharedComponents/confirmDialog";
import TokenExpire from "components/logout/tokenExpire";
import lazy from "utils/lazy";
import config from "assets/config";
import { getAppMode, isWebsiteUnderMaintenance } from "utils/maintenance";
import { logout } from "utils/common";
import HFNAuth from "components/hfnauth";
import SelectRole from "components/selectRole";

// lazy components
const LayoutContainer = lazy("layouts/Template");
const DepositDonationSummary = lazy("donation/depositDonation/Summary");
const MobileThermalView = lazy("donation/donationEntry/MobileThermalView");

const App = () => {
  const [underMaintenance, setUnderMaintenance] = useState(false);
  const isNoCrawling =
    config?.appMode === "staging" || config?.appMode === "development";
  const elementRef = useRef(null);

  useEffect(() => {
    import("hfnauth-web/main");
  });

  const handleUserLoggedOut = useCallback(
    async (event) => {
      if (event?.detail?.loggedOut) {
        await logout();
      }
    },
    [logout],
  );

  useEffect(() => {
    if (typeof window !== "undefined") {
      import("hfnauth-web/main").then(() => {
        elementRef.current = document.querySelector("hfn-auth");

        if (elementRef.current) {
          elementRef.current.addEventListener(
            "userLoggedOut",
            handleUserLoggedOut,
          );
        }

        return () => {
          if (elementRef.current) {
            elementRef.current.removeEventListener(
              "userLoggedOut",
              handleUserLoggedOut,
            );
          }
        };
      });
    }
  }, [elementRef]);

  useEffect(() => {
    isWebsiteUnderMaintenance().then((res) => setUnderMaintenance(res));
  }, []);

  useEffect(() => {
    import("hfn-appmode/main");
  }, []);

  useEffect(() => {
    if (underMaintenance) {
      import("hfn-maintenance-web/main");
    }
  }, [underMaintenance]);

  return (
    <HFNErrorBoundary>
      {isNoCrawling && (
        <Helmet>
          <meta name="robots" content="noindex" />
        </Helmet>
      )}
      <HFNLoader>
        <hfn-auth
          ref={elementRef}
          config={JSON.stringify(config?.keyCloakAuthConfig)}
          showCancel
          authType={
            window.location.pathname !== "/unauthorized" ? "" : "on-demand"
          }
        />
        <div className="hfn">
          <hfn-app-mode
            appMode={getAppMode(config?.appMode)}
            displayOn="topMiddle"
          />
          {underMaintenance ? (
            <hfn-maintenance-web />
          ) : (
            <Routes>
              <Route path="/hfnauth/authorization" element={<HFNAuth />} />
              <Route path="/login" element={<Login />} />
              <Route path="/select-role" element={<SelectRole />} />
              <Route
                path="/abhyasi-donors-general-information"
                element={<AbhaysForm />}
              />
              <Route
                path="*"
                element={<AuthGuard path="/" component={LayoutContainer} />}
              />

              <Route
                path="/deposit-donation-summary"
                element={
                  <AuthGuard
                    path="/deposit-donation-summary"
                    component={DepositDonationSummary}
                  />
                }
              />

              <Route
                path="/mobile-thermal-view"
                element={
                  <AuthGuard
                    path="/mobile-thermal-view"
                    component={MobileThermalView}
                  />
                }
              />
            </Routes>
          )}
        </div>

        <div>
          <HFNToaster />
          <HFNConfirmDialog />
          <TokenExpire />
        </div>
      </HFNLoader>
    </HFNErrorBoundary>
  );
};

export default App;
